import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sparkles, Send, Rocket, X, ArrowRight } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";

// In-app version of the §11 first-win email sequence: one contextual nudge that
// pulls new creators toward their next milestone (create -> publish -> grow/upgrade).
// Reuses GET /growth/onboarding (single source of truth) — no competing data.
const STAGES = {
  create: {
    icon: Send, tint: "from-[#3b9eff] to-[#1f6feb]", cta: "Create my first game", to: "/app/create",
    title: "What game will you create first?",
    body: "Every great game begins with one idea — “A racing game inside a volcano.” Describe it and Studio builds the starting world.",
  },
  publish: {
    icon: Rocket, tint: "from-[#f59e0b] to-[#ec4899]", cta: "Publish to the Arcade", to: null,
    title: "Your game is only the beginning",
    body: "Nice — you built a game! Publish it to the GameView Arcade so friends and players can jump in and play.",
  },
  grow: {
    icon: Sparkles, tint: "from-[#7c3aed] to-[#a855f7]", cta: "Take my game further", to: "/pricing",
    title: "Let the world play — then grow it",
    body: "Share your game, list an item in the Marketplace, or unlock more AI power with Creator Pro to keep building.",
  },
};

export default function FirstWinNudge() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [stage, setStage] = useState(null);
  const [route, setRoute] = useState(null);
  const [hidden, setHidden] = useState(false);

  useEffect(() => {
    if (user && ["admin", "superadmin"].includes(user.role)) return; // platform staff don't need the first-win nudge
    api.get("/growth/onboarding").then((r) => {
      const steps = r.data?.steps || [];
      if (r.data?.complete) return; // fully onboarded — no nudge
      const by = Object.fromEntries(steps.map((s) => [s.key, s]));
      let key = "create", rt = "/app/create";
      if (by.create?.done && !by.publish?.done) { key = "publish"; rt = by.publish?.route || "/app/create"; }
      else if (by.publish?.done) { key = "grow"; rt = "/pricing"; }
      if (localStorage.getItem(`gv_firstwin_dismissed_${key}`)) return;
      setStage(key); setRoute(rt);
    }).catch(() => {});
  }, [user]);

  if (!stage || hidden) return null;
  const s = STAGES[stage];
  const Icon = s.icon;
  const dismiss = () => { localStorage.setItem(`gv_firstwin_dismissed_${stage}`, "1"); setHidden(true); };

  return (
    <div className="relative mb-5 rounded-2xl overflow-hidden border border-[hsl(var(--border))]" data-testid="firstwin-nudge">
      <div className={`absolute inset-0 bg-gradient-to-r ${s.tint} opacity-[0.14]`} />
      <div className="relative flex items-center gap-4 p-4 sm:p-5">
        <span className={`w-11 h-11 rounded-xl grid place-items-center bg-gradient-to-br ${s.tint} shrink-0 shadow-lg`}>
          <Icon className="w-5 h-5 text-white" />
        </span>
        <div className="flex-1 min-w-0">
          <h3 className="font-display text-base sm:text-lg font-bold leading-tight">{s.title}</h3>
          <p className="text-xs sm:text-sm text-[hsl(var(--muted-foreground))] mt-0.5 line-clamp-2">{s.body}</p>
        </div>
        <Button onClick={() => navigate(route || s.to)} data-testid="firstwin-cta"
          className="rounded-full shrink-0 bg-gradient-to-r from-[#1f6feb] to-[#3b9eff] hidden sm:inline-flex">
          {s.cta} <ArrowRight className="w-4 h-4" />
        </Button>
        <button onClick={dismiss} className="shrink-0 w-8 h-8 grid place-items-center rounded-full hover:bg-black/10" data-testid="firstwin-dismiss" aria-label="Dismiss">
          <X className="w-4 h-4 text-[hsl(var(--muted-foreground))]" />
        </button>
      </div>
      <button onClick={() => navigate(route || s.to)} className="sm:hidden w-full py-2.5 text-sm font-bold bg-gradient-to-r from-[#1f6feb] to-[#3b9eff] text-white flex items-center justify-center gap-1" data-testid="firstwin-cta-mobile">
        {s.cta} <ArrowRight className="w-4 h-4" />
      </button>
    </div>
  );
}
